/** `Button`'s variant and size contracts as DATA: the record `Button.tsx` derives every style from, rather than a set
 * of parallel maps the compiler cannot hold to each other.
 *
 * Both records are total (`Record<ButtonVariant, ...>`, `Record<ComponentSize, ...>`) and every facet on the value type
 * is required, so a ninth variant is a compile error until it has answered resting colours, hover repaint, hover
 * elevation, backdrop filter and extra treatment. Three facets used to sit OUTSIDE any total map -- a
 * `Partial<Record<ButtonVariant, string>>` for hover background, a `Set<ButtonVariant>` for the no-shadow variants, and
 * a per-variant style bag -- and only the first of the three would even have been noticed missing.
 */

import { colors } from '../../css-refs.colors.ts';
import { spacing } from '../../css-refs.spacing.ts';
import { typography } from '../../css-refs.typography.ts';
import { borderRadius, glass } from '../../css-refs.effects.ts';
import { BORDER_DEFAULT, TEXT_LINK_BASE_STYLE } from '../../common/style-primitives.ts';
import type { ButtonRadius, ButtonVariantFacets, ButtonVariant } from '@federa/types/ui-button';
import type { ComponentSize, ComponentSizeFacets } from '@federa/types/ui-design-tokens';

/**
 * Variant -> full facet set. `hoverBackground` repeating `background` is the deliberate spelling of "this variant does
 * not repaint on hover": `link` and `default` reach it that way, and under the previous `Partial` map both instead
 * resolved to `undefined`, which cleared the resting background React had already applied.
 */
export const BUTTON_VARIANT_FACETS: Record<ButtonVariant, ButtonVariantFacets> = {
	primary: {
		background: colors.brand.primary,
		foreground: colors.background.primary,
		border: 'none',
		// THE BRAND'S OWN DARKER STEP, not a status colour. `status.success.dark` carries the same hex as `primary.dark`
		// in the canonical tree, so a swap shows nothing there -- but on an expression whose brand is ink and whose status
		// colours stay green (Noor) it hovers the primary button to the success green. A brand control's hover is a
		// brand token; status tokens are for status.
		hoverBackground: colors.primary.dark,
		hoverShadow: true,
		backdropFilter: null,
		extra: null,
	},
	secondary: {
		background: colors.brand.secondary,
		foreground: colors.background.primary,
		border: 'none',
		hoverBackground: colors.text.primary,
		hoverShadow: true,
		backdropFilter: null,
		extra: null,
	},
	outline: {
		background: 'transparent',
		foreground: colors.brand.primary,
		border: BORDER_DEFAULT,
		hoverBackground: colors.background.secondary,
		hoverShadow: true,
		backdropFilter: null,
		extra: null,
	},
	ghost: {
		background: 'transparent',
		foreground: colors.text.primary,
		border: 'none',
		hoverBackground: colors.background.tertiary,
		hoverShadow: false,
		backdropFilter: null,
		extra: null,
	},
	destructive: {
		background: colors.semantic.error,
		foreground: colors.background.primary,
		border: 'none',
		hoverBackground: colors.status.error.dark,
		hoverShadow: true,
		backdropFilter: null,
		extra: null,
	},
	link: {
		background: 'transparent',
		foreground: colors.brand.primary,
		border: 'none',
		// Inline text: transparent at rest and transparent under the pointer.
		hoverBackground: 'transparent',
		hoverShadow: false,
		backdropFilter: null,
		extra: { ...TEXT_LINK_BASE_STYLE, textUnderlineOffset: spacing[1] },
	},
	glass: {
		background: colors.overlay.glass,
		foreground: colors.text.secondary,
		border: 'none',
		hoverBackground: colors.overlay.semiTransparent,
		hoverShadow: false,
		backdropFilter: glass.backdropFilter,
		extra: null,
	},
	default: {
		background: colors.background.tertiary,
		foreground: colors.text.primary,
		border: BORDER_DEFAULT,
		// Repaints to its own resting fill: the tinted surface stays put under the pointer, and the elevation shadow
		// below is what marks the hover. Absent from the old map, this is where the transparent-on-hover bug lived.
		hoverBackground: colors.background.tertiary,
		hoverShadow: true,
		backdropFilter: null,
		extra: null,
	},
};

/**
 * The `hoverFill` sentinel meaning "write NO hover layer at all" -- neither the repaint nor the elevation shadow.
 *
 * It is a distinct answer from passing the resting fill back in, and both are needed. A caller whose resting fill comes
 * from its own `style` can spell "repaint to X" by passing X; a caller whose hover is CONDITIONALLY absent (the
 * `aria-current` page in `components/navigation/Pagination.tsx`, which the injected CSS excluded with
 * `:not([aria-current="page"])`) has no colour to name -- it needs the layer to not exist, shadow included.
 *
 * Typed as a plain `string` on the prop rather than `string | 'none'`: TypeScript ABSORBS a literal into `string`, so
 * the union is the same type with extra syntax and `no-redundant-type-constituents` rejects it.
 */
export const BUTTON_HOVER_FILL_NONE = 'none';

/** Radius -> corner token, total over `ButtonRadius` (iss-ad97d4c3): the pill the button always drew, and the two
 *  squarer corners a chip beside squared surfaces asks for -- the same shape as `BADGE_RADIUS_TOKENS`. */
export const BUTTON_RADIUS_TOKENS: Record<ButtonRadius, string> = {
	full: borderRadius.full,
	md: borderRadius.md,
	sm: borderRadius.sm,
};

/** Size -> box metrics. `height: 'auto'` lets padding drive the box; an icon-only button squares itself to this value. */
export const BUTTON_SIZE_FACETS: Record<ComponentSize, ComponentSizeFacets> = {
	sm: {
		padding: `${spacing[2]} ${spacing[4]}`,
		fontSize: typography.fontSize.xs,
		height: 'auto',
	},
	md: {
		padding: `${spacing[3]} ${spacing[5]}`,
		fontSize: typography.fontSize.sm,
		height: 'auto',
	},
	lg: {
		padding: `${spacing[4]} ${spacing[6]}`,
		fontSize: typography.fontSize.base,
		height: 'auto',
	},
};
